import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TableSortLabel,
} from '@mui/material';
import {
  CloudUpload as ExportIcon,
  CloudDownload as ImportIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { rangeosApiService } from '../services/rangeos-api';
import { basePrefixApi, EnvironmentWithBucket } from '../services/api';

interface GhostTraffic {
  uuid: string;
  name: string;
  description?: string;
  author?: string;
  dateCreated?: string;
  dateEdited?: string;
  type?: any;
}

interface S3File {
  key: string;
  name: string;
  size: number;
  lastModified: string;
}

export const GhostTrafficManager: React.FC = () => {
  // Environment state
  const [environments, setEnvironments] = useState<any[]>([]);
  const [s3Environments, setS3Environments] = useState<EnvironmentWithBucket[]>([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');
  const [selectedS3Environment, setSelectedS3Environment] = useState<string>('');

  // Tab state
  const [activeTab, setActiveTab] = useState<number>(0);

  // Data state
  const [ghostTrafficList, setGhostTrafficList] = useState<GhostTraffic[]>([]);
  const [s3Files, setS3Files] = useState<S3File[]>([]);
  const [selectedTraffic, setSelectedTraffic] = useState<Set<string>>(new Set());
  const [selectedS3Files, setSelectedS3Files] = useState<Set<string>>(new Set());

  // Loading state
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  // Messages
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter and sort state for Ghost Traffic
  const [trafficFilter, setTrafficFilter] = useState<string>('');
  const [trafficSortField, setTrafficSortField] = useState<'name' | 'author' | 'handler'>('name');
  const [trafficSortDirection, setTrafficSortDirection] = useState<'asc' | 'desc'>('asc');

  // Filter and sort state for S3 Files
  const [s3Filter, setS3Filter] = useState<string>('');
  const [s3SortField, setS3SortField] = useState<'name' | 'size' | 'lastModified'>('name');
  const [s3SortDirection, setS3SortDirection] = useState<'asc' | 'desc'>('asc');

  // Load environments on mount
  useEffect(() => {
    loadEnvironments();
  }, []);

  // Load ghost traffic when environment changes
  useEffect(() => {
    if (selectedEnvironment) {
      loadGhostTraffic();
    } else {
      setGhostTrafficList([]);
    }
  }, [selectedEnvironment]);

  // Load S3 files when S3 environment changes
  useEffect(() => {
    if (selectedS3Environment) {
      loadS3Files();
    } else {
      setS3Files([]);
    }
  }, [selectedS3Environment]);

  const loadEnvironments = async () => {
    setLoading(true);
    setError(null);
    try {
      const [endpoints, s3Envs] = await Promise.all([
        rangeosApiService.getEndpoints(),
        basePrefixApi.getEnvironmentsWithBuckets()
      ]);
      setEnvironments(endpoints);
      setS3Environments(s3Envs);
    } catch (err: any) {
      setError(`Failed to load environments: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadGhostTraffic = async () => {
    if (!selectedEnvironment) return;
    setLoading(true);
    setError(null);
    try {
      const traffic = await rangeosApiService.listGhostTraffic(selectedEnvironment);
      setGhostTrafficList(traffic);
      setSelectedTraffic(new Set());
    } catch (err: any) {
      setError(`Failed to load ghost traffic: ${err.message}`);
      setGhostTrafficList([]);
    } finally {
      setLoading(false);
    }
  };

  const loadS3Files = async () => {
    if (!selectedS3Environment) return;
    setLoading(true);
    setError(null);
    try {
      const files = await rangeosApiService.listGhostTrafficFilesFromS3(selectedS3Environment);
      setS3Files(files);
      setSelectedS3Files(new Set());
    } catch (err: any) {
      setError(`Failed to load S3 files: ${err.message}`);
      setS3Files([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportToS3 = async () => {
    if (selectedTraffic.size === 0 || !selectedS3Environment) {
      setError('Please select ghost traffic profiles and an S3 destination');
      return;
    }

    setExporting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await rangeosApiService.batchExportGhostTrafficToS3(
        selectedEnvironment,
        {
          targetEnvironment: selectedS3Environment,
          uuids: Array.from(selectedTraffic),
        }
      );

      if (result.success) {
        setSuccess(`Successfully exported ${result.exported.length} ghost traffic profile(s) to S3`);
        setSelectedTraffic(new Set());
        // Refresh S3 files list
        loadS3Files();
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleImportFromS3 = async () => {
    if (selectedS3Files.size === 0 || !selectedEnvironment) {
      setError('Please select S3 files and a target RangeOS environment');
      return;
    }

    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await rangeosApiService.batchImportGhostTrafficFromS3(
        selectedEnvironment,
        {
          s3Keys: Array.from(selectedS3Files),
        }
      );

      if (result.success) {
        setSuccess(`Successfully imported ${result.imported.length} ghost traffic profile(s)`);
        setSelectedS3Files(new Set());
        // Refresh ghost traffic list
        loadGhostTraffic();
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleExportToLocal = async () => {
    if (selectedTraffic.size === 0 || !selectedEnvironment) {
      setError('Please select ghost traffic profiles to export');
      return;
    }

    setExporting(true);
    setError(null);
    setSuccess(null);

    try {
      const uuids = Array.from(selectedTraffic);
      for (const uuid of uuids) {
        const traffic = await rangeosApiService.getGhostTraffic(selectedEnvironment, uuid);
        const trafficInfo = ghostTrafficList.find(t => t.uuid === uuid);
        const filename = `ghost_traffic_${(trafficInfo?.name || uuid).replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.json`;
        const blob = new Blob([JSON.stringify(traffic, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      setSuccess(`Exported ${uuids.length} ghost traffic profile(s) to local files`);
    } catch (err: any) {
      setError(`Local export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const toggleTrafficSelection = (uuid: string) => {
    const newSelection = new Set(selectedTraffic);
    if (newSelection.has(uuid)) {
      newSelection.delete(uuid);
    } else {
      newSelection.add(uuid);
    }
    setSelectedTraffic(newSelection);
  };

  const toggleS3FileSelection = (key: string) => {
    const newSelection = new Set(selectedS3Files);
    if (newSelection.has(key)) {
      newSelection.delete(key);
    } else {
      newSelection.add(key);
    }
    setSelectedS3Files(newSelection);
  };

  // Filter and sort Ghost Traffic
  const filteredAndSortedTraffic = React.useMemo(() => {
    let filtered = ghostTrafficList;

    // Apply filter
    if (trafficFilter) {
      const lowerFilter = trafficFilter.toLowerCase();
      filtered = filtered.filter(t =>
        t.name?.toLowerCase().includes(lowerFilter) ||
        t.description?.toLowerCase().includes(lowerFilter) ||
        t.author?.toLowerCase().includes(lowerFilter) ||
        t.type?.handler?.toLowerCase().includes(lowerFilter) ||
        t.uuid?.toLowerCase().includes(lowerFilter)
      );
    }

    // Apply sort
    return [...filtered].sort((a, b) => {
      let aVal: string = '';
      let bVal: string = '';

      switch (trafficSortField) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'author':
          aVal = a.author?.toLowerCase() || '';
          bVal = b.author?.toLowerCase() || '';
          break;
        case 'handler':
          aVal = a.type?.handler?.toLowerCase() || '';
          bVal = b.type?.handler?.toLowerCase() || '';
          break;
      }

      if (trafficSortDirection === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });
  }, [ghostTrafficList, trafficFilter, trafficSortField, trafficSortDirection]);

  // Filter and sort S3 files
  const filteredAndSortedS3Files = React.useMemo(() => {
    let filtered = s3Files;

    // Apply filter
    if (s3Filter) {
      const lowerFilter = s3Filter.toLowerCase();
      filtered = filtered.filter(f =>
        f.name?.toLowerCase().includes(lowerFilter) ||
        f.key?.toLowerCase().includes(lowerFilter)
      );
    }

    // Apply sort
    return [...filtered].sort((a, b) => {
      if (s3SortField === 'name') {
        const aVal = a.name?.toLowerCase() || '';
        const bVal = b.name?.toLowerCase() || '';
        return s3SortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else if (s3SortField === 'size') {
        return s3SortDirection === 'asc' ? a.size - b.size : b.size - a.size;
      } else {
        const aDate = new Date(a.lastModified).getTime();
        const bDate = new Date(b.lastModified).getTime();
        return s3SortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
    });
  }, [s3Files, s3Filter, s3SortField, s3SortDirection]);

  const handleTrafficSort = (field: 'name' | 'author' | 'handler') => {
    if (trafficSortField === field) {
      setTrafficSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setTrafficSortField(field);
      setTrafficSortDirection('asc');
    }
  };

  const handleS3Sort = (field: 'name' | 'size' | 'lastModified') => {
    if (s3SortField === field) {
      setS3SortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setS3SortField(field);
      setS3SortDirection('asc');
    }
  };

  const selectAllTraffic = () => {
    if (selectedTraffic.size === filteredAndSortedTraffic.length) {
      setSelectedTraffic(new Set());
    } else {
      setSelectedTraffic(new Set(filteredAndSortedTraffic.map(t => t.uuid)));
    }
  };

  const selectAllS3Files = () => {
    if (selectedS3Files.size === filteredAndSortedS3Files.length) {
      setSelectedS3Files(new Set());
    } else {
      setSelectedS3Files(new Set(filteredAndSortedS3Files.map(f => f.key)));
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Ghost Traffic Manager
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Export Ghost Traffic profiles from RangeOS to S3, or import from S3 backups.
      </Typography>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Environment Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuration
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>RangeOS Environment</InputLabel>
              <Select
                value={selectedEnvironment}
                onChange={(e) => setSelectedEnvironment(e.target.value)}
                label="RangeOS Environment"
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Select environment</em>
                </MenuItem>
                {environments.map((env) => (
                  <MenuItem key={env.id} value={env.id}>
                    {env.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>S3 Environment</InputLabel>
              <Select
                value={selectedS3Environment}
                onChange={(e) => setSelectedS3Environment(e.target.value)}
                label="S3 Environment"
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Select S3 environment</em>
                </MenuItem>
                {s3Environments.map((env) => (
                  <MenuItem key={env.environmentId} value={env.prefix}>
                    {env.environmentName} ({env.bucketName})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab
          label={`Export to S3 (${ghostTrafficList.length})`}
          icon={<ExportIcon />}
          iconPosition="start"
        />
        <Tab
          label={`Import from S3 (${s3Files.length})`}
          icon={<ImportIcon />}
          iconPosition="start"
        />
      </Tabs>

      {/* Export Tab */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Ghost Traffic in RangeOS
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Refresh list">
                  <IconButton onClick={loadGhostTraffic} disabled={!selectedEnvironment || loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportToLocal}
                  disabled={selectedTraffic.size === 0 || exporting}
                >
                  {exporting ? 'Exporting...' : `Download ${selectedTraffic.size}`}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ExportIcon />}
                  onClick={handleExportToS3}
                  disabled={selectedTraffic.size === 0 || !selectedS3Environment || exporting}
                >
                  {exporting ? 'Exporting...' : `Export ${selectedTraffic.size} to S3`}
                </Button>
              </Box>
            </Box>

            {!selectedEnvironment ? (
              <Alert severity="info">Select a RangeOS environment to view Ghost Traffic profiles</Alert>
            ) : ghostTrafficList.length === 0 ? (
              <Alert severity="info">No Ghost Traffic profiles found in this environment</Alert>
            ) : (
              <>
                {/* Search/Filter */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Filter by name, author, handler, or UUID..."
                    value={trafficFilter}
                    onChange={(e) => setTrafficFilter(e.target.value)}
                    sx={{ width: 350 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: trafficFilter && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setTrafficFilter('')}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {trafficFilter && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      Showing {filteredAndSortedTraffic.length} of {ghostTrafficList.length}
                    </Typography>
                  )}
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedTraffic.size === filteredAndSortedTraffic.length && filteredAndSortedTraffic.length > 0}
                            indeterminate={selectedTraffic.size > 0 && selectedTraffic.size < filteredAndSortedTraffic.length}
                            onChange={selectAllTraffic}
                          />
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={trafficSortField === 'name'}
                            direction={trafficSortField === 'name' ? trafficSortDirection : 'asc'}
                            onClick={() => handleTrafficSort('name')}
                          >
                            Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={trafficSortField === 'author'}
                            direction={trafficSortField === 'author' ? trafficSortDirection : 'asc'}
                            onClick={() => handleTrafficSort('author')}
                          >
                            Author
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={trafficSortField === 'handler'}
                            direction={trafficSortField === 'handler' ? trafficSortDirection : 'asc'}
                            onClick={() => handleTrafficSort('handler')}
                          >
                            Handler
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>UUID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAndSortedTraffic.map((traffic) => (
                        <TableRow
                          key={traffic.uuid}
                          selected={selectedTraffic.has(traffic.uuid)}
                          hover
                          onClick={() => toggleTrafficSelection(traffic.uuid)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={selectedTraffic.has(traffic.uuid)} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {traffic.name}
                            </Typography>
                            {traffic.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {traffic.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {traffic.author || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={traffic.type?.handler || 'unknown'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {traffic.uuid.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredAndSortedTraffic.length === 0 && trafficFilter && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No Ghost Traffic profiles match the filter "{trafficFilter}"
                  </Alert>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Ghost Traffic Backups in S3
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Refresh list">
                  <IconButton onClick={loadS3Files} disabled={!selectedS3Environment || loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={!selectedEnvironment || importing}
                >
                  {importing ? 'Importing...' : 'Import Local File'}
                  <input
                    type="file"
                    hidden
                    accept=".json"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file && selectedEnvironment) {
                        setImporting(true);
                        setError(null);
                        setSuccess(null);
                        file.text().then(content => {
                          const ghostTrafficData = JSON.parse(content);
                          return rangeosApiService.importGhostTrafficFromJson(selectedEnvironment, ghostTrafficData);
                        }).then(() => {
                          setSuccess(`Successfully imported ghost traffic from ${file.name}`);
                          loadGhostTraffic();
                        }).catch(err => {
                          setError(`Import failed: ${err.message}`);
                        }).finally(() => {
                          setImporting(false);
                        });
                        e.target.value = '';
                      }
                    }}
                  />
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ImportIcon />}
                  onClick={handleImportFromS3}
                  disabled={selectedS3Files.size === 0 || !selectedEnvironment || importing}
                >
                  {importing ? 'Importing...' : `Import ${selectedS3Files.size} to RangeOS`}
                </Button>
              </Box>
            </Box>

            {!selectedS3Environment ? (
              <Alert severity="info">Select an S3 environment to view Ghost Traffic backups</Alert>
            ) : s3Files.length === 0 ? (
              <Alert severity="info">No Ghost Traffic backups found in this S3 environment</Alert>
            ) : (
              <>
                {/* Search/Filter */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Filter by file name..."
                    value={s3Filter}
                    onChange={(e) => setS3Filter(e.target.value)}
                    sx={{ width: 350 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: s3Filter && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setS3Filter('')}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {s3Filter && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      Showing {filteredAndSortedS3Files.length} of {s3Files.length}
                    </Typography>
                  )}
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedS3Files.size === filteredAndSortedS3Files.length && filteredAndSortedS3Files.length > 0}
                            indeterminate={selectedS3Files.size > 0 && selectedS3Files.size < filteredAndSortedS3Files.length}
                            onChange={selectAllS3Files}
                          />
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={s3SortField === 'name'}
                            direction={s3SortField === 'name' ? s3SortDirection : 'asc'}
                            onClick={() => handleS3Sort('name')}
                          >
                            File Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={s3SortField === 'size'}
                            direction={s3SortField === 'size' ? s3SortDirection : 'asc'}
                            onClick={() => handleS3Sort('size')}
                          >
                            Size
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={s3SortField === 'lastModified'}
                            direction={s3SortField === 'lastModified' ? s3SortDirection : 'asc'}
                            onClick={() => handleS3Sort('lastModified')}
                          >
                            Last Modified
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAndSortedS3Files.map((file) => (
                        <TableRow
                          key={file.key}
                          selected={selectedS3Files.has(file.key)}
                          hover
                          onClick={() => toggleS3FileSelection(file.key)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={selectedS3Files.has(file.key)} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <StorageIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {file.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatBytes(file.size)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(file.lastModified).toLocaleString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredAndSortedS3Files.length === 0 && s3Filter && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No S3 files match the filter "{s3Filter}"
                  </Alert>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
